import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { useTheme } from '../contexts/ThemeContext';
import { whatsappService, WhatsAppMessageLog } from '../services/whatsapp';
import { orgSettingsService } from '../services/orgSettings';
import { MessageCircle, ChevronLeft, ChevronRight, CheckCircle, XCircle, RefreshCw, Settings, X } from 'lucide-react';
import { SkeletonTable, EmptyState } from '../components/LoadingStates';

export const WhatsAppLogs: React.FC = () => {
    const { currentTheme } = useTheme();
    const [page, setPage] = useState(0);
    const pageSize = 20;
    const [autoSend, setAutoSend] = useState(false);
    const [savingAutoSend, setSavingAutoSend] = useState(false);
    const [showSettings, setShowSettings] = useState(false);

    const { data, isLoading, isFetching, refetch } = useQuery(
        ['whatsapp-logs', page],
        () => whatsappService.getLogs(page, pageSize),
        { keepPreviousData: true }
    );

    const { data: orgSettings } = useQuery(
        ['orgEmailSettings'],
        () => orgSettingsService.getEmailSettings(),
        { staleTime: 30_000 }
    );

    const queryClient = useQueryClient();
    const [waEnabled, setWaEnabled] = useState(false);
    const [otpRequested, setOtpRequested] = useState(false);
    const [otpMaskedEmail, setOtpMaskedEmail] = useState('');
    const [otpValue, setOtpValue] = useState('');
    const [requestingOtp, setRequestingOtp] = useState(false);
    const [verifyingOtp, setVerifyingOtp] = useState(false);

    useEffect(() => {
        if (orgSettings) {
            setAutoSend(orgSettings.whatsappAutoSend ?? false);
            setWaEnabled(orgSettings.whatsappEnabled ?? false);
        }
    }, [orgSettings]);

    const toggleAutoSend = async () => {
        if (!orgSettings || savingAutoSend || !waEnabled) return;
        const next = !autoSend;
        setAutoSend(next);
        setSavingAutoSend(true);
        try {
            await orgSettingsService.saveEmailSettings({ ...orgSettings, whatsappAutoSend: next });
            queryClient.invalidateQueries(['orgEmailSettings']);
            toast.success(next
                ? 'Bills will be sent on WhatsApp automatically after settle'
                : 'Auto-send disabled — you will be asked after each settle');
        } catch {
            setAutoSend(!next);
            toast.error('Failed to update setting');
        } finally {
            setSavingAutoSend(false);
        }
    };

    // Enabling WhatsApp requires email-OTP verification; disabling is direct
    const handleEnableToggle = async () => {
        if (!orgSettings || requestingOtp) return;
        if (waEnabled) {
            try {
                await orgSettingsService.saveEmailSettings({ ...orgSettings, whatsappEnabled: false, whatsappAutoSend: false });
                setWaEnabled(false);
                setAutoSend(false);
                setOtpRequested(false);
                queryClient.invalidateQueries(['orgEmailSettings']);
                toast.success('WhatsApp disabled — all WhatsApp features are now off');
            } catch {
                toast.error('Failed to disable WhatsApp');
            }
            return;
        }
        try {
            setRequestingOtp(true);
            const res = await whatsappService.requestEnableOtp();
            setOtpRequested(true);
            setOtpValue('');
            setOtpMaskedEmail(res.email || '');
            toast.success(`OTP sent to ${res.email || 'your organization email'}`);
        } catch (e: any) {
            toast.error(e.response?.data?.message || 'Failed to send OTP');
        } finally {
            setRequestingOtp(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (otpValue.trim().length !== 6) {
            toast.error('Enter the 6-digit OTP');
            return;
        }
        try {
            setVerifyingOtp(true);
            await whatsappService.verifyEnableOtp(otpValue.trim());
            setWaEnabled(true);
            setOtpRequested(false);
            setOtpValue('');
            queryClient.invalidateQueries(['orgEmailSettings']);
            toast.success('WhatsApp enabled for your organization!');
        } catch (e: any) {
            toast.error(e.response?.data?.message || 'Invalid OTP');
        } finally {
            setVerifyingOtp(false);
        }
    };

    const logs = data?.content ?? [];
    const sentOnPage = logs.filter(l => l.status === 'SENT').length;
    const failedOnPage = logs.filter(l => l.status === 'FAILED').length;

    const triggerLabel = (t: WhatsAppMessageLog['trigger']) =>
        t === 'SETTLE_AUTO' ? 'Auto (Settle)' : 'Manual Share';

    return (
        <div className="min-h-screen p-3 sm:p-4 md:p-6">
            <div className="space-y-4 sm:space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#25D36622' }}>
                            <MessageCircle className="h-5 w-5" style={{ color: '#25D366' }} />
                        </div>
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold" style={{ color: currentTheme.colors.text.primary }}>
                                WhatsApp Logs
                            </h1>
                            <p className="text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                                Every WhatsApp message sent — your billing record
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => refetch()}
                            disabled={isFetching}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50"
                            style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.primary, backgroundColor: currentTheme.colors.background.card }}
                        >
                            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                        <button
                            onClick={() => setShowSettings(true)}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors"
                            style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.primary, backgroundColor: currentTheme.colors.background.card }}
                        >
                            <Settings className="h-4 w-4" />
                            Settings
                        </button>
                    </div>
                </div>

                {/* Summary cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg border" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
                        <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Messages</p>
                        <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
                            {data?.totalElements ?? 0}
                        </p>
                    </div>
                    <div className="p-4 rounded-lg border" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
                        <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Sent (this page)</p>
                        <p className="text-2xl font-bold mt-1 text-green-600">{sentOnPage}</p>
                    </div>
                    <div className="p-4 rounded-lg border" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
                        <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Failed (this page)</p>
                        <p className="text-2xl font-bold mt-1 text-red-600">{failedOnPage}</p>
                    </div>
                </div>

                {/* Logs table */}
                <div className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] overflow-hidden" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
                    {isLoading ? (
                        <div className="p-4"><SkeletonTable rows={8} columns={6} /></div>
                    ) : logs.length === 0 ? (
                        <EmptyState
                            icon={<MessageCircle className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
                            title="No WhatsApp messages yet"
                            description="Messages sent on settlement or via Share Bill on WhatsApp will appear here"
                        />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
                                <thead className="ui-thead">
                                    <tr>
                                        {['Date & Time', 'Order#', 'Mobile', 'Template', 'Trigger', 'Status'].map(h => (
                                            <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="ui-tbody">
                                    {logs.map(log => (
                                        <tr key={log.id}>
                                            <td className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: currentTheme.colors.text.primary }}>
                                                {format(new Date(log.createdAt), 'dd MMM yyyy, hh:mm a')}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                                                {log.orderNo || '—'}
                                            </td>
                                            <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>
                                                {log.mobile}
                                            </td>
                                            <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                                                {log.templateName}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <span className="px-2 py-0.5 rounded-full text-xs font-medium"
                                                    style={{
                                                        backgroundColor: log.trigger === 'SETTLE_AUTO' ? currentTheme.colors.primary[100] : '#fef3c7',
                                                        color: log.trigger === 'SETTLE_AUTO' ? currentTheme.colors.primary[700] : '#b45309',
                                                    }}>
                                                    {triggerLabel(log.trigger)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                {log.status === 'SENT' ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                                        <CheckCircle className="h-3 w-3" /> Sent
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700"
                                                        title={log.error || undefined}
                                                    >
                                                        <XCircle className="h-3 w-3" /> Failed
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {(data?.totalPages ?? 0) > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
                            <p className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                                Page {(data?.number ?? 0) + 1} of {data?.totalPages} · {data?.totalElements} messages
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setPage(p => Math.max(0, p - 1))}
                                    disabled={page === 0 || isFetching}
                                    className="p-2 rounded-lg border transition-colors disabled:opacity-40"
                                    style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.primary }}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => setPage(p => p + 1)}
                                    disabled={page >= (data?.totalPages ?? 1) - 1 || isFetching}
                                    className="p-2 rounded-lg border transition-colors disabled:opacity-40"
                                    style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.primary }}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* WhatsApp Settings Modal */}
            {showSettings && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
                    <div
                        className="max-w-md w-full rounded-xl border p-5"
                        style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#25D36622' }}>
                                    <Settings className="h-5 w-5" style={{ color: '#25D366' }} />
                                </div>
                                <h3 className="text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                                    WhatsApp Settings
                                </h3>
                            </div>
                            <button
                                onClick={() => setShowSettings(false)}
                                className="p-1 rounded-lg transition-colors"
                                style={{ backgroundColor: currentTheme.colors.background.secondary }}
                            >
                                <X className="h-5 w-5" style={{ color: currentTheme.colors.text.secondary }} />
                            </button>
                        </div>

                        {/* 1) Master switch — requires email OTP to enable */}
                        <div className="p-4 rounded-lg border" style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: currentTheme.colors.background.secondary }}>
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-sm font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                                        Enable WhatsApp
                                    </p>
                                    <p className="text-xs mt-1 leading-5" style={{ color: currentTheme.colors.text.secondary }}>
                                        Master switch for all WhatsApp features (share bill, auto-send).
                                        Enabling requires an OTP sent to your organization's registered email.
                                    </p>
                                </div>
                                <button
                                    onClick={handleEnableToggle}
                                    disabled={requestingOtp || !orgSettings}
                                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none flex-shrink-0 mt-1 disabled:opacity-60"
                                    style={{ backgroundColor: waEnabled ? '#25D366' : '#d1d5db' }}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${waEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>

                            {/* OTP entry step */}
                            {!waEnabled && otpRequested && (
                                <div className="mt-3 pt-3 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
                                    <p className="text-xs mb-2" style={{ color: currentTheme.colors.text.secondary }}>
                                        Enter the 6-digit OTP sent to <span className="font-semibold">{otpMaskedEmail || 'your organization email'}</span>
                                    </p>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={otpValue}
                                            onChange={e => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            placeholder="••••••"
                                            className="flex-1 px-3 py-2 border rounded-lg text-sm tracking-[6px] text-center font-bold focus:outline-none focus:ring-2"
                                            style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: currentTheme.colors.background.card, color: currentTheme.colors.text.primary }}
                                        />
                                        <button
                                            onClick={handleVerifyOtp}
                                            disabled={verifyingOtp || otpValue.length !== 6}
                                            className="px-4 py-2 rounded-lg text-white text-sm font-semibold transition-colors disabled:opacity-50"
                                            style={{ backgroundColor: '#25D366' }}
                                        >
                                            {verifyingOtp ? 'Verifying…' : 'Verify'}
                                        </button>
                                    </div>
                                    <button
                                        onClick={handleEnableToggle}
                                        disabled={requestingOtp}
                                        className="mt-2 text-xs font-medium hover:opacity-70 transition-opacity disabled:opacity-50"
                                        style={{ color: currentTheme.colors.primary[600] }}
                                    >
                                        {requestingOtp ? 'Sending…' : 'Resend OTP'}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* 2) Auto-send — only meaningful when WhatsApp is enabled */}
                        <div
                            className="flex items-start justify-between gap-4 p-4 rounded-lg border mt-3"
                            style={{
                                borderColor: currentTheme.colors.border.primary,
                                backgroundColor: currentTheme.colors.background.secondary,
                                opacity: waEnabled ? 1 : 0.5,
                            }}
                        >
                            <div>
                                <p className="text-sm font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                                    Send bill automatically after settle
                                </p>
                                <p className="text-xs mt-1 leading-5" style={{ color: currentTheme.colors.text.secondary }}>
                                    <span className="font-semibold">ON:</span> every settled order's bill is sent to the customer's WhatsApp automatically (message charges apply).{' '}
                                    <span className="font-semibold">OFF:</span> after each settle you'll be asked whether to share the bill.
                                    {!waEnabled && <span className="block mt-1 font-medium">Enable WhatsApp first to use this.</span>}
                                </p>
                            </div>
                            <button
                                onClick={toggleAutoSend}
                                disabled={savingAutoSend || !orgSettings || !waEnabled}
                                className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none flex-shrink-0 mt-1 disabled:opacity-60"
                                style={{ backgroundColor: autoSend ? '#25D366' : '#d1d5db' }}
                            >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${autoSend ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                        </div>

                        <button
                            onClick={() => setShowSettings(false)}
                            className="w-full mt-4 py-2.5 rounded-lg text-sm font-semibold border transition-colors"
                            style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.primary }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
